const num = v => { const n = Number(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : 0; };
const EPS = 1e-8;
const stock = v => !v?.available ? 0 : v.inventory_management && v.inventory_policy !== 'continue' ? Math.max(0, num(v.inventory_quantity)) : Infinity;
/** Estado de compra (snippets/product-state.liquid): «comprable» (o sin dato) y «preventa» van al carrito (la preventa con su
 * propiedad de línea «Entrega», ADR 0010); «proximamente» se cotiza y avisa. */
export const isComing = v => v?.state === 'proximamente';
export const isPreorder = v => v?.state === 'preventa';
export const canBuy = v => !v?.state || v.state === 'comprable' || v.state === 'preventa';
const lineFor = v => (v.delivery?.key && v.delivery?.value ? { properties: { [v.delivery.key]: v.delivery.value } } : {});
const fill = (s, values) => String(s ?? '').replace(/\[([^\]]+)\]/g, (_, key) => values[key] ?? '');
export function normalizeState(raw = {}) {
  return { largo: Math.min(100, Math.max(0,num(raw.largo ?? 6))), alto: Math.max(0,num(raw.alto ?? 2)), profundidad: Math.max(0,num(raw.profundidad ?? .6)), peso: Math.max(0,num(raw.peso ?? 200)), armado: raw.armado !== false && raw.armado !== '0' };
}
/** T-041 (ADR 0017): precio de cada línea con el escalón de custom.packs (variante: packs y pack_family) según la cantidad total
 * de su familia (Packs.priceLines, assets/packs.js). Sin packs.js o sin packs, precio × cantidad. Devuelve el total de los
 * productos por transferencia o null si ninguna línea tiene precio por transferencia. */
export function applyPacks(items) {
  const Packs = globalThis.Packs;
  if (!Packs || !items.length) return null;
  const priced = Packs.priceLines(items.map(i => ({ packs: i.variant.packs, family: i.variant.pack_family, price: num(i.variant.price), qty: i.qty })));
  items.forEach((i, k) => { const line = priced.lines[k]; Object.assign(i, { subtotal: line.subtotal, unit: line.unit, packMin: line.min, transferSubtotal: line.transferSubtotal }); });
  return priced.hasTransfer ? priced.transfer : null;
}
export function computePlan(variants, raw) {
  const state = normalizeState(raw);
  const empty = { status:'sin-modelo', n:0, items:[], productos:0, armadoQty:0 };
  if (!state.largo || !state.alto || !state.profundidad || !state.peso) return empty;
  const valid = variants.filter(v => num(v.alto)>0 && num(v.alto)<=state.alto+EPS && num(v.profundidad)>0 && num(v.profundidad)<=state.profundidad+EPS && num(v.ancho_modulo)>0 && num(v.carga)>=state.peso && Number.isInteger(num(v.niveles)) && num(v.niveles)>0 && Number.isInteger(num(v.price)) && num(v.price)>=0);
  const plans = valid.filter(v => v.rol === 'inicial').map(v => {
    const n = Math.floor((state.largo+EPS)/num(v.ancho_modulo));
    if (!n) return empty;
    // Only explicit families can share uprights; standalone kits repeat the initial.
    const continuations = v.familia ? valid.filter(c => c.rol==='continuacion' && c.familia===v.familia && ['alto','ancho_modulo','profundidad','niveles','carga'].every(k => Math.abs(num(c[k])-num(v[k]))<EPS)) : [];
    const c = continuations.find(c => stock(c)>=n-1) ?? continuations[0];
    const items = [{variant:v,qty:c?1:n}, ...(c && n>1 ? [{variant:c,qty:n-1}]:[])].map(i => ({...i, subtotal:num(i.variant.price)*i.qty, short:Math.max(0,i.qty-stock(i.variant))}));
    const transferProductos = applyPacks(items);
    const unitCapacity = num(v.carga)*num(v.niveles);
    const coming = items.some(i=>isComing(i.variant));
    // Próximamente: no se descuenta stock (todavía no hay), el precio es orientativo y nada va al carrito.
    const short = coming ? false : items.some(i=>i.short);
    return { status:short?'falta-stock':'ok', coming, comingHandles:[...new Set(items.filter(i=>isComing(i.variant)).map(i=>i.variant.handle).filter(Boolean))], n, items, transferProductos, niveles:num(v.niveles), capacidad:n*(num(v.carga_total)>0 ? Math.min(unitCapacity,num(v.carga_total)):unitCapacity), metrosModulos:n*num(v.ancho_modulo), productos:items.reduce((s,i)=>s+i.subtotal,0), armadoQty:Math.ceil(items.reduce((s,i)=>s+num(i.variant.unidades_armado)*i.qty,0)-EPS), assemblyKnown:items.every(i=>i.variant.unidades_armado!=null && num(i.variant.unidades_armado)>0), carga:num(v.carga), alto:num(v.alto) };
  }).filter(p=>p.n);
  // Primero lo que se puede comprar ya (ok y a la venta), después próximamente; luego la pared más ocupada, capacidad, alto y precio.
  plans.sort((a,b)=>(a.status!=='ok')-(b.status!=='ok') || a.coming-b.coming || b.metrosModulos-a.metrosModulos || b.capacidad-a.capacidad || b.alto-a.alto || a.carga-b.carga || a.productos-b.productos);
  return plans[0] ?? empty;
}
export function assemblyReady(plan, armado) { return plan.assemblyKnown && armado?.id && stock(armado)>=plan.armadoQty; }
/** Líneas para /cart/add.js. Vacío si falta stock, si algún producto es próximamente (tampoco el armado) o si el armado no está listo. */
export function cartItems(plan, armado, enabled) {
  if (plan.status!=='ok' || plan.coming || plan.items.some(i=>!canBuy(i.variant)) || (enabled && !assemblyReady(plan,armado))) return [];
  return [...plan.items.map(i=>({id:i.variant.id,quantity:i.qty,...lineFor(i.variant)})), ...(enabled && plan.armadoQty>0 ? [{id:armado.id,quantity:plan.armadoQty}]:[])];
}
export function totals(plan, armado, enabled) { const cost=enabled ? plan.armadoQty*num(armado?.price):0; return {productos:plan.productos,armado:cost,total:plan.productos+cost}; }
const Base = globalThis.HTMLElement ?? class {};
export class CalcEstanterias extends Base {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.data = JSON.parse(this.querySelector('[data-calc-data]').textContent);
    this.strings = this.data.strings;
    this.variants = this.data.variants;
    this.armado = this.data.armado;
    this.fmt = {money:v=>new Intl.NumberFormat('es-UY',{style:'currency',currency:'UYU',maximumFractionDigits:0}).format(v/100),meters:v=>new Intl.NumberFormat('es-UY',{maximumFractionDigits:2}).format(v)};
    this.form = this.querySelector('[data-calc-form]');
    this.result = this.querySelector('[data-calc-result]');
    this.state = normalizeState(Object.fromEntries(new URLSearchParams(location.search)));
    if (!this.armado) this.state.armado = false;
    for (const key of ['largo','alto','profundidad','peso']) this.form.elements[key].value=this.state[key];
    const preset=this.form.elements.preset; if(preset) preset.value=[...preset.options].some(o=>o.value===String(this.state.peso)) ? String(this.state.peso) : '';
    this.notify=this.querySelector('[data-calc-notify]');
    this.form.addEventListener('submit',e=>e.preventDefault());
    this.form.addEventListener('input',e=>{
      if(e.target.name==='peso') this.form.elements.preset.value='';
      if(e.target.name==='preset' && e.target.value) this.form.elements.peso.value=e.target.value;
      this.state=normalizeState({...Object.fromEntries(new FormData(this.form)),armado:this.state.armado});
      this.update(); this.track('calc_change',this.analyticsPayload());
    });
    this.result.addEventListener('change',e=>{if(e.target.name==='armado'){this.state.armado=e.target.checked;this.update();}});
    this.result.addEventListener('click',e=>{if(e.target.closest('[data-calc-add]'))this.addToCart();if(e.target.closest('[data-calc-whatsapp]'))this.track('calc_whatsapp',this.analyticsPayload());});
    this.update(true); this.track('calc_view',this.analyticsPayload());
  }
  update(initial=false) {
    const url=new URL(location.href);
    for(const [k,v] of Object.entries(this.state)) url.searchParams.set(k,typeof v==='boolean' ? (v?'1':'0'):v);
    this.shareUrl=url.href;
    if(!initial) history.replaceState(history.state,'',url);
    this.plan=computePlan(this.variants,this.state);
    this.renderResult(this.plan);
    if(!this.plan.n && this.data.whatsapp){const a=document.createElement('a');a.textContent=this.strings.whatsapp;a.href=`https://wa.me/${this.data.whatsapp}?text=${encodeURIComponent(this.shareUrl)}`;this.result.append(a);}
  }
  renderResult(plan) {
    const s = this.strings;
    const { money, meters } = this.fmt;
    const el = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    };

    const live = this.querySelector('[data-calc-live]');
    if (!plan.n) {
      this.result.replaceChildren(el('p', 'calc__hint', s.vacio));
      live.textContent = s.vacio;
      return;
    }

    const summary = el(
      'p',
      'calc__summary',
      fill(s.resumen, {
        n: plan.n,
        niveles: plan.niveles,
        capacidad: plan.capacidad,
        metros: meters(plan.metrosModulos),
        ocupado: meters(plan.largoOcupado),
      }),
    );

    const list = el('ul', 'calc__list');
    for (const item of plan.items) {
      const li = el('li', 'calc__item');
      const name = el('span', 'calc__item-name');
      name.append(el('span', 'calc__qty', `${item.qty} ×`), ' ');
      if (item.variant) {
        const link = el('a', null, item.variant.title);
        link.href = item.variant.url;
        name.append(link);
        if (isComing(item.variant)) name.append(el('span', 'calc__badge calc__badge--coming', s.proximamente));
        else if (isPreorder(item.variant) && s.preventa) name.append(el('span', 'calc__badge calc__badge--preventa', s.preventa));
      } else {
        name.append(el('span', null, s[`rol_${item.rol}`]));
      }
      li.append(name, el('span', 'calc__money', item.variant ? money(item.subtotal) : '—'));
      // T-041: con el escalón de un pack (5 o más de la familia), el precio por unidad que lo explica.
      if (item.variant && item.packMin > 1 && this.data.packs?.unit) li.append(el('span', 'calc__unit', fill(this.data.packs.unit, { price: money(item.unit) })));
      if (!item.variant) li.append(el('span', 'calc__warn', s.sin_modelo));
      else if (item.short > 0 && !plan.coming) {
        const stock = item.qty - item.short;
        li.append(el('span', 'calc__warn', stock > 0 ? fill(s.falta_stock, { stock }) : s.sin_stock));
      }
      list.append(li);
    }

    const t = totals(plan, this.armado, this.state.armado);
    const rows = el('dl', 'calc__totals');
    const row = (label, value, className) => {
      const wrap = el('div', className);
      wrap.append(el('dt', null, label), el('dd', 'calc__money', value));
      rows.append(wrap);
    };
    row(plan.coming ? s.orientativo : s.productos, money(t.productos));

    const armadoBox = el('div', 'calc__armado');
    if (this.armado) {
      const toggle = el('label', 'calc__switch');
      const input = el('input');
      input.type = 'checkbox';
      input.name = 'armado';
      input.checked = this.state.armado;
      toggle.append(input, el('span', null, s.armado_toggle));
      armadoBox.append(toggle);
      if (this.state.armado && plan.armadoQty > 0) {
        armadoBox.append(
          el('p', 'calc__hint', fill(s.armado_detalle, { unidades: plan.armadoQty, tarifa: money(this.armado?.price ?? 0) })),
        );
        row(s.armado, money(t.armado));
      }
    }
    row(plan.coming ? s.total_orientativo : this.state.armado && t.armado ? s.total_con_armado : s.total, money(t.total), 'calc__total');

    // T-041: total por transferencia = productos al precio por transferencia del escalón de su familia (custom.packs) + armado.
    const packs = this.data.packs;
    let transferNote = null;
    if (!plan.coming && plan.transferProductos != null && packs) {
      const wrap = el('div', 'calc__transfer');
      wrap.append(el('dt', null, packs.label), el('dd', 'calc__money', money(plan.transferProductos + t.armado)));
      rows.append(wrap);
      transferNote = el('p', 'calc__transfer-note', packs.note);
    }

    const actions = el('div', 'calc__actions');
    const items = cartItems(plan, this.armado, this.state.armado);
    if (plan.coming && this.notify) {
      // Próximamente: en vez de «Agregar todo al carrito», el form «Avisame cuando llegue» con un tag aviso-{handle} por producto.
      // El form puede seguir en su <template> (notify.js lo clona al abrir): los tags van al elemento y al input si ya existe.
      const tags = ['aviso', ...plan.comingHandles.map((h) => `aviso-${h}`)].join(',');
      const box = this.notify.querySelector('notify-form');
      if (box?.setTags) box.setTags(tags); else if (box) box.dataset.tags = tags;
      this.notify.hidden = false;
      actions.append(this.notify);
    } else {
      if (this.notify) { this.notify.hidden = true; this.append(this.notify); }
      const add = el('button', 'calc__btn calc__btn--primary', s.agregar);
      add.type = 'button';
      add.dataset.calcAdd = '';
      add.disabled = items.length === 0;
      actions.append(add);
    }
    if (this.data.whatsapp) {
      const wa = el('a', 'calc__btn calc__btn--secondary', s.whatsapp);
      wa.dataset.calcWhatsapp = '';
      wa.target = '_blank';
      wa.rel = 'noopener';
      wa.href = `https://wa.me/${this.data.whatsapp}?text=${encodeURIComponent(
        whatsappText({ plan, state: this.state, armado: this.armado, strings: s, fmt: this.fmt, link: this.shareUrl }),
      )}`;
      actions.append(wa);
    }

    const status = el('p', 'calc__status');
    status.dataset.calcStatus = '';
    status.setAttribute('role', 'status');
    if (this.state.armado && !assemblyReady(plan, this.armado)) status.textContent = s.estado_sin_modelo;
    if (plan.status === 'falta-stock') status.textContent = s.estado_falta_stock;
    if (plan.status === 'sin-modelo') status.textContent = s.estado_sin_modelo;
    if (plan.coming) status.textContent = s.estado_proximamente;
    else if (plan.items.some(i => isPreorder(i.variant)) && s.estado_preventa) status.textContent = s.estado_preventa;

    this.result.replaceChildren(summary, list, armadoBox, rows, ...(transferNote ? [transferNote] : []), el('p', 'calc__iva', s.iva), status, actions);
    const totalLabel = this.state.armado && t.armado ? s.total_con_armado : s.total;
    live.textContent = `${summary.textContent} ${totalLabel}: ${money(t.total)}. ${status.textContent}`.trim();
  }

  async addToCart() {
    const items = cartItems(this.plan, this.armado, this.state.armado);
    if (!items.length) return;
    const button = this.result.querySelector('[data-calc-add]');
    const status = this.result.querySelector('[data-calc-status]');
    if (!button) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.textContent = this.strings.agregando;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.description || body.message || this.strings.error);
      this.track('calc_add_to_cart', { ...this.analyticsPayload(), items });
      document.dispatchEvent(new CustomEvent('cart:items-added', { detail: { source: 'calc-estanterias', items: body.items ?? items } }));
      const drawer = document.querySelector('cart-drawer');
      if (typeof drawer?.open === 'function') {
        drawer.open();
        status.textContent = this.strings.agregado;
      } else {
        status.replaceChildren(this.strings.agregado, ' ');
        const link = document.createElement('a');
        link.href = this.data.cartUrl || '/cart';
        link.textContent = this.strings.ver_carrito;
        status.append(link);
      }
    } catch (error) {
      status.textContent = error.message || this.strings.error;
    } finally {
      if (button.isConnected) button.disabled = cartItems(this.plan, this.armado, this.state.armado).length === 0;
      button.removeAttribute('aria-busy');
    }
  }

  analyticsPayload(){return {calculadora:'estanterias',...this.state,modulos:this.plan.n,total:totals(this.plan,this.armado,this.state.armado).total/100};}
  track(name,payload){try{window.Shopify?.analytics?.publish?.(name,payload);}catch{}}
}
export function whatsappText({plan,state,armado,strings:s,fmt,link}) { return [s.wa_intro,fill(s.wa_config,{largo:fmt.meters(state.largo),alto:fmt.meters(state.alto),profundidad:fmt.meters(state.profundidad),peso:state.peso}),fill(s.resumen,{n:plan.n,niveles:plan.niveles,capacidad:plan.capacidad,metros:fmt.meters(plan.metrosModulos)}),...plan.items.map(i=>`${i.qty} × ${i.variant.title}: ${fmt.money(i.subtotal)}`),state.armado ? `${s.armado}: ${plan.armadoQty} × ${fmt.money(armado?.price??0)}`:'',`${s.total}: ${fmt.money(totals(plan,armado,state.armado).total)}`,plan.transferProductos!=null && !plan.coming && s.wa_transfer ? fill(s.wa_transfer,{total:fmt.money(plan.transferProductos+totals(plan,armado,state.armado).armado)}):'',s.iva,link].filter(Boolean).join('\n'); }
if(globalThis.customElements && !customElements.get('calc-estanterias'))customElements.define('calc-estanterias',CalcEstanterias);
